///
/// EarthBrowser
///
/// Created by Matt Giger on 12/6/2025
/// Copyright (c) 2025 Geodata Labs
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { wgs } from "@/earthbrowser/utils/geographic"
import { type Camera } from "@stovokor/camera/Camera"
import { type CameraState } from "@stovokor/camera/CameraState"
import { type Earth } from "../Earth"
import { EarthCameraState } from "../EarthCameraState"
import { EarthDropPinHandler } from "./EarthDropPinHandler"
import { StoryDrawHandler } from "./StoryDrawHandler"
import { EarthPanHandler } from "./EarthPanHandler"
import { EarthDoubleTapHandler, EarthSingleTapHandler } from "./EarthTapHandler"
import { EarthTapDragZoomHandler } from "./EarthTapDragZoomHandler"
import { EarthTwoFingerHandler } from "./EarthTwoFingerHandler"
import { EarthZoomHandler } from "./EarthZoomHandler"

export class EarthCameraAdapter {
	private context: EarthBrowserContext
	private earth: Earth
	private cameraState: EarthCameraState

	private tapHandler: EarthSingleTapHandler
	private doubleTapHandler?: EarthDoubleTapHandler
	private tapDragZoomHandler?: EarthTapDragZoomHandler
	private panHandler: EarthPanHandler
	private zoomHandler?: EarthZoomHandler
	private twoFingerHandler?: EarthTwoFingerHandler

	constructor(context: EarthBrowserContext, earth: Earth) {
		this.context = context
		this.earth = earth

		this.cameraState = new EarthCameraState(context)

		this.tapHandler = new EarthSingleTapHandler(context)
		this.earth.addRecognizer(this.tapHandler)
		this.tapHandler.onGesture(() => {
			this.context.focusManager.requestFocus(this.earth)
		})

		this.panHandler = new EarthPanHandler(context, this.cameraState)
		this.earth.addRecognizer(this.panHandler)

		// Authoring gestures don't exist in restricted contexts (embeds)
		if (!context.restrictedInteraction) {
			// Long-press / right-click drop-pin menu (placemark creation). Wires
			// itself onto the earth node and canvas; lives for the app lifetime.
			new EarthDropPinHandler(context, earth, this.cameraState)

			// Story annotation click-to-draw (enabled only while the editor has
			// a drawing in progress). Lives for the app lifetime.
			new StoryDrawHandler(earth)
		}
	}

	/** Live camera state, shared with automated motion (CameraDirector) */
	public get earthCameraState(): EarthCameraState {
		return this.cameraState
	}

	public attach(camera: Camera): void {
		this.cameraState.activate(camera)

		this.doubleTapHandler = new EarthDoubleTapHandler(this.context, this.cameraState)
		this.earth.addRecognizer(this.doubleTapHandler)
		this.tapHandler.requireToFail = [this.doubleTapHandler]

		// Tap-then-drag zoom; a second tap that lifts still stays a double tap
		this.tapDragZoomHandler = new EarthTapDragZoomHandler(this.context, this.cameraState)
		this.earth.addRecognizer(this.tapDragZoomHandler)

		// Wheel zoom; two-finger touch is the two-finger handler's
		this.zoomHandler = new EarthZoomHandler(this.context, this.cameraState)
		this.earth.addRecognizer(this.zoomHandler)

		// Pinch / twist / two-finger pan and the two-finger tilt swipe
		this.twoFingerHandler = new EarthTwoFingerHandler(this.context, this.cameraState)
		this.earth.addRecognizer(this.twoFingerHandler)
	}

	public detach(): void {
		this.cameraState.deactivate()

		this.earth.removeRecognizer(this.doubleTapHandler)
		this.doubleTapHandler = undefined
		this.tapHandler.requireToFail = []

		this.earth.removeRecognizer(this.tapDragZoomHandler)
		this.tapDragZoomHandler = undefined

		this.earth.removeRecognizer(this.zoomHandler)
		this.zoomHandler = undefined
		this.earth.removeRecognizer(this.twoFingerHandler)
		this.twoFingerHandler = undefined
	}

	public update(): void {
		this.cameraState.renderUpdate()
	}

	public getEarthCameraState(): CameraState {
		return this.cameraState.getTargetCameraState()
	}

	public animatedTiltAndBearing(tilt: number, bearing: number): boolean {
		this.cameraState.animatedTiltAndBearing(tilt, bearing)
		return true
	}

	public animatedCameraBearing(bearing: number): boolean {
		this.cameraState.animatedCameraBearing(bearing)
		return true
	}

	public animatedCameraTilt(targetTilt: number): boolean {
		return this.cameraState.animatedCameraTilt(targetTilt)
	}

	public animatedFlyToLocation(lat: number, lon: number, altitude?: number): boolean {
		this.cameraState.flyToLocation(lat, lon, altitude)
		return true
	}

	public animatedZoomIn(): boolean {
		// Zoom by a fraction of the current altitude so the step feels consistent
		// at any scale (roughly halves the altitude)
		const altitude = this.cameraState.spherical.radius - wgs.a
		this.cameraState.moveForward(altitude * 0.5)
		return true
	}

	public animatedZoomOut(): boolean {
		// Roughly doubles the altitude
		const altitude = this.cameraState.spherical.radius - wgs.a
		this.cameraState.moveForward(-altitude)
		return true
	}

	public animatedZoomTo(altitude: number): boolean {
		this.cameraState.moveForward(altitude)
		return true
	}

	public immediateCameraTilt(tilt: number): void {
		this.cameraState.immediateCameraTilt(tilt)
	}

	public immediateCameraBearing(bearing: number): void {
		this.cameraState.immediateCameraBearing(bearing)
	}
}
